import classNames from 'classnames';
import {
    generateMaxCopies,
    generateReserveRatios,
    getCopyFormulas,
    mapCopyFormula,
    mapMaxCopiesValueToKey,
    mapReserveRatio
} from '../constants';
import {
    ReserveSettingsGenerateReportContainer,
    RSColumn,
    RSLoanFormat,
    RSMaxCopiesRow
} from './ReserveSettingsComponents';
import { ReserveCheckBox } from './ReserveCheckBox';
import { LOANFORMAT_EAUDIOBOOKS, LOANFORMAT_EBOOKS } from '../../../store/loanFormat/actions';
import React, { useState } from 'react';
import { FormulaKey, MaxCopies, SupportedLoanFormat } from './ReserveListDetailPopup';
import { Trans, useTranslation } from 'react-i18next';
import { DropDownMenu } from '../../../shared/common/DropDownMenu';
import { useSelector } from 'react-redux';
import { AppState } from '../../../store/appState';

export const RESERVE_RATIOS = generateReserveRatios();
export const MAX_COPIES = generateMaxCopies();

type LoanFormatSettings = {
    copyFormula: FormulaKey;
    maxCopies: MaxCopies;
    reserveRatio: number;
    selected: boolean;
};

export type ReserveReportSettingsState = {
    [LOANFORMAT_EBOOKS]: LoanFormatSettings;
    [LOANFORMAT_EAUDIOBOOKS]: LoanFormatSettings;
    editMode: boolean;
    isHintShown: boolean;
};

interface Props {
    state: ReserveReportSettingsState;
    onLoanformatCheckChange: (checkState: boolean, loanFormat: SupportedLoanFormat) => void;
    onSelectReserveRatio: (reserveRatio: number, loanFormat: SupportedLoanFormat) => void;
    onSelectMaxCopies: (maxCopies: MaxCopies, loanFormat: SupportedLoanFormat) => void;
    onSelectFormula: (formulaKey: FormulaKey, loanFormat: SupportedLoanFormat) => void;
}

export const ReserveReportSettings = (props: Props) => {
    const [ebookRatioExpanded, setEbookRatioExpanded] = useState(false);
    const [ebookMaxCopiesExpanded, setEbookMaxCopiesExpanded] = useState(false);
    const [ebookFormulaExpanded, setEbookFormulaExpanded] = useState(false);
    const [eaudiobookRatioExpanded, setEaudiobookRatioExpanded] = useState(false);
    const [eaudiobookMaxCopiesExpanded, setEaudiobookMaxCopiesExpanded] = useState(false);
    const [eaudiobookFormulaExpanded, setEaudiobookFormulaExpanded] = useState(false);
    const { t } = useTranslation();
    const { state, onLoanformatCheckChange, onSelectReserveRatio, onSelectMaxCopies, onSelectFormula } = props;
    const isCustomFormulaEnabled = useSelector((state: AppState) => state.authentication.spoc.customCopyFormulaExists);

    const dropdownBooksButtonClass = classNames(
        state.editMode && state.EBOOKS.selected ? 'enabled-dropdown' : 'disabled-dropdown',
        !state.EBOOKS.selected ? 'disabled-text' : ''
    );
    const dropdownAudioButtonClass = classNames(
        state.editMode && state.EAUDIOBOOKS.selected ? 'enabled-dropdown' : 'disabled-dropdown',
        !state.EAUDIOBOOKS.selected ? 'disabled-text' : ''
    );

    const formulas = getCopyFormulas(isCustomFormulaEnabled);
    const ebookCopyFormula = mapCopyFormula(state.EBOOKS.copyFormula);
    const eaudiobookCopyFormula = mapCopyFormula(state.EAUDIOBOOKS.copyFormula);
    const eBookReserveRatio = mapReserveRatio(state.EBOOKS.reserveRatio, RESERVE_RATIOS);
    const EbookRatioLabel = () => <span className="selectedValue">{eBookReserveRatio}</span>;

    const ebookMaxCopyValue = mapMaxCopiesValueToKey(state.EBOOKS.maxCopies);
    const EbookMaxCopiesLabel = () => (
        <span>
            <span className="selectedValue">
                {t(`reserveReports.defaultSettings.maxCopies.${ebookMaxCopyValue}`, {
                    defaultValue: ebookMaxCopyValue
                })}
            </span>
        </span>
    );

    const EbookFormulaLabel = () => (
        <span className="selectedValue">
            {ebookCopyFormula?.value
                ? t(`reserveReports.defaultSettings.copyFormulaKey.${ebookCopyFormula.key.toLowerCase()}`, {
                      defaultValue: ebookCopyFormula.value
                  })
                : null}
        </span>
    );
    const EAudioBookRatioLabel = () => (
        <span className="selectedValue">{mapReserveRatio(state.EAUDIOBOOKS.reserveRatio, RESERVE_RATIOS)}</span>
    );

    const eAudiobookMaxCopyValue = mapMaxCopiesValueToKey(state.EAUDIOBOOKS.maxCopies);
    const EAudioBookMaxCopiesLabel = () => (
        <span className="selectedValue">
            {t(`reserveReports.defaultSettings.maxCopies.${eAudiobookMaxCopyValue}`, {
                defaultValue: eAudiobookMaxCopyValue
            })}
        </span>
    );

    const EAudiobookFormulaLabel = () => (
        <span className="selectedValue">
            {eaudiobookCopyFormula?.value
                ? t(`reserveReports.defaultSettings.copyFormulaKey.${eaudiobookCopyFormula.key.toLowerCase()}`, {
                      defaultValue: eaudiobookCopyFormula.value
                  })
                : null}
        </span>
    );

    return (
        <ReserveSettingsGenerateReportContainer>
            <RSColumn>
                <RSLoanFormat>
                    <ReserveCheckBox
                        isEnabled={true}
                        isChecked={state.EBOOKS.selected}
                        text={t('menu.shop.ebooksMain')}
                        onClick={state => {
                            onLoanformatCheckChange(state, LOANFORMAT_EBOOKS);
                        }}
                    />
                </RSLoanFormat>
                <RSMaxCopiesRow>
                    <DropDownMenu
                        expanded={ebookRatioExpanded}
                        gap={2}
                        onCloseListener={() => setEbookRatioExpanded(false)}
                        onToggleClick={() => setEbookRatioExpanded(!ebookRatioExpanded)}
                        onClickOutside={() => setEbookRatioExpanded(false)}
                        align="right"
                        displacement={50}
                        label={
                            <Trans
                                i18nKey="reserveReports.reservesPerCopyLimit"
                                components={{ 1: <EbookRatioLabel /> }}
                            />
                        }
                        btnInnerClassName="gen-report-popup-dropdown"
                        btnClassName={dropdownBooksButtonClass}
                        className="fl-none"
                        dataDisplayname={'EbookCopyLimitDropdown'}
                    >
                        {RESERVE_RATIOS.map((ratio, i) =>
                            state.EBOOKS.reserveRatio !== ratio.key ? (
                                <li
                                    className="gen-report-popup-dropdown-list-element"
                                    key={i}
                                    onClick={() => {
                                        onSelectReserveRatio(ratio.key, LOANFORMAT_EBOOKS);
                                        setEbookRatioExpanded(false);
                                    }}
                                >
                                    {ratio.value}
                                </li>
                            ) : null
                        )}
                    </DropDownMenu>
                </RSMaxCopiesRow>
                <br />
                <RSMaxCopiesRow>
                    <DropDownMenu
                        expanded={ebookMaxCopiesExpanded}
                        gap={2}
                        onCloseListener={() => setEbookMaxCopiesExpanded(false)}
                        onToggleClick={() => setEbookMaxCopiesExpanded(!ebookMaxCopiesExpanded)}
                        onClickOutside={() => setEbookMaxCopiesExpanded(false)}
                        align="right"
                        displacement={50}
                        label={
                            <Trans
                                i18nKey="reserveReports.maxCopiesInCollection"
                                components={{ 1: <EbookMaxCopiesLabel /> }}
                            />
                        }
                        btnInnerClassName="gen-report-popup-dropdown"
                        btnClassName={dropdownBooksButtonClass}
                        className="fl-none"
                        dataDisplayname={'EbookMaxCopiesDropdown'}
                    >
                        {MAX_COPIES.map((maxCopy, i) =>
                            state.EBOOKS.maxCopies !== maxCopy ? (
                                <li
                                    className="gen-report-popup-dropdown-list-element"
                                    key={i}
                                    onClick={() => {
                                        onSelectMaxCopies(maxCopy as MaxCopies, LOANFORMAT_EBOOKS);
                                        setEbookMaxCopiesExpanded(false);
                                    }}
                                >
                                    {t(`reserveReports.defaultSettings.maxCopies.${maxCopy}`, {
                                        defaultValue: maxCopy
                                    })}
                                </li>
                            ) : null
                        )}
                    </DropDownMenu>
                </RSMaxCopiesRow>
                <br />
                <RSMaxCopiesRow>
                    <DropDownMenu
                        expanded={ebookFormulaExpanded}
                        gap={2}
                        onCloseListener={() => setEbookFormulaExpanded(false)}
                        onToggleClick={() => setEbookFormulaExpanded(!ebookFormulaExpanded)}
                        onClickOutside={() => setEbookFormulaExpanded(false)}
                        align="right"
                        label={
                            <Trans
                                i18nKey="reserveReports.calculationOfAdditionalCopies"
                                components={{ 1: <EbookFormulaLabel /> }}
                            />
                        }
                        btnInnerClassName="gen-report-popup-dropdown"
                        btnClassName={dropdownBooksButtonClass}
                        className="fl-none"
                        dataDisplayname={'EbookCalculationDropdown'}
                    >
                        {formulas.map((formula, i) =>
                            state.EBOOKS.copyFormula !== formula.key ? (
                                <li
                                    className="gen-report-popup-dropdown-list-element"
                                    key={i}
                                    onClick={() => {
                                        onSelectFormula(formula.key as FormulaKey, LOANFORMAT_EBOOKS);
                                        setEbookFormulaExpanded(false);
                                    }}
                                >
                                    {t(`reserveReports.defaultSettings.copyFormulaKey.${formula.key.toLowerCase()}`, {
                                        defaultValue: formula.value
                                    })}
                                </li>
                            ) : null
                        )}
                    </DropDownMenu>
                </RSMaxCopiesRow>
            </RSColumn>
            <RSColumn>
                <RSLoanFormat>
                    <ReserveCheckBox
                        isEnabled={true}
                        isChecked={state.EAUDIOBOOKS.selected}
                        text={t('menu.shop.eaudiobooksMain')}
                        onClick={state => {
                            onLoanformatCheckChange(state, LOANFORMAT_EAUDIOBOOKS);
                        }}
                    />
                </RSLoanFormat>
                <RSMaxCopiesRow>
                    <DropDownMenu
                        expanded={eaudiobookRatioExpanded}
                        gap={2}
                        onCloseListener={() => setEaudiobookRatioExpanded(false)}
                        onToggleClick={() => setEaudiobookRatioExpanded(!eaudiobookRatioExpanded)}
                        onClickOutside={() => setEaudiobookRatioExpanded(false)}
                        align="right"
                        displacement={50}
                        label={
                            <Trans
                                i18nKey="reserveReports.reservesPerCopyLimit"
                                components={{ 1: <EAudioBookRatioLabel /> }}
                            />
                        }
                        btnInnerClassName="gen-report-popup-dropdown"
                        btnClassName={dropdownAudioButtonClass}
                        className="fl-none"
                        dataDisplayname={'EaudiobookCopyLimitDropdown'}
                    >
                        {RESERVE_RATIOS.map((ratio, i) =>
                            state.EAUDIOBOOKS.reserveRatio !== ratio.key ? (
                                <li
                                    className="gen-report-popup-dropdown-list-element"
                                    key={i}
                                    onClick={() => onSelectReserveRatio(ratio.key, LOANFORMAT_EAUDIOBOOKS)}
                                >
                                    {ratio.value}
                                </li>
                            ) : null
                        )}
                    </DropDownMenu>
                </RSMaxCopiesRow>
                <br />
                <RSMaxCopiesRow>
                    <DropDownMenu
                        expanded={eaudiobookMaxCopiesExpanded}
                        gap={2}
                        onCloseListener={() => setEaudiobookMaxCopiesExpanded(false)}
                        onToggleClick={() => setEaudiobookMaxCopiesExpanded(!eaudiobookMaxCopiesExpanded)}
                        onClickOutside={() => setEaudiobookMaxCopiesExpanded(false)}
                        align="right"
                        displacement={50}
                        label={
                            <Trans
                                i18nKey="reserveReports.maxCopiesInCollection"
                                components={{ 1: <EAudioBookMaxCopiesLabel /> }}
                            />
                        }
                        btnInnerClassName="gen-report-popup-dropdown"
                        btnClassName={dropdownAudioButtonClass}
                        className="fl-none"
                        dataDisplayname={'EaudiobookMaxCopiesDropdown'}
                    >
                        {MAX_COPIES.map((maxCopy, i) =>
                            state.EAUDIOBOOKS.maxCopies !== maxCopy ? (
                                <li
                                    className="gen-report-popup-dropdown-list-element"
                                    key={i}
                                    onClick={() => onSelectMaxCopies(maxCopy as MaxCopies, LOANFORMAT_EAUDIOBOOKS)}
                                >
                                    {t(`reserveReports.defaultSettings.maxCopies.${maxCopy}`, {
                                        defaultValue: maxCopy
                                    })}
                                </li>
                            ) : null
                        )}
                    </DropDownMenu>
                </RSMaxCopiesRow>
                <br />
                <RSMaxCopiesRow>
                    <DropDownMenu
                        expanded={eaudiobookFormulaExpanded}
                        gap={2}
                        onCloseListener={() => setEaudiobookFormulaExpanded(false)}
                        onToggleClick={() => setEaudiobookFormulaExpanded(!eaudiobookFormulaExpanded)}
                        onClickOutside={() => setEaudiobookFormulaExpanded(false)}
                        align="right"
                        label={
                            <Trans
                                i18nKey="reserveReports.calculationOfAdditionalCopies"
                                components={{ 1: <EAudiobookFormulaLabel /> }}
                            />
                        }
                        btnInnerClassName="gen-report-popup-dropdown"
                        btnClassName={dropdownAudioButtonClass}
                        className="fl-none"
                        dataDisplayname={'EaudiobookCalculationDropdown'}
                    >
                        {formulas.map((formula, i) =>
                            state.EAUDIOBOOKS.copyFormula !== formula.key ? (
                                <li
                                    className="gen-report-popup-dropdown-list-element"
                                    key={i}
                                    onClick={() => {
                                        onSelectFormula(formula.key as FormulaKey, LOANFORMAT_EAUDIOBOOKS);
                                        setEaudiobookFormulaExpanded(false);
                                    }}
                                >
                                    {t(`reserveReports.defaultSettings.copyFormulaKey.${formula.key.toLowerCase()}`, {
                                        defaultValue: formula.value
                                    })}
                                </li>
                            ) : null
                        )}
                    </DropDownMenu>
                </RSMaxCopiesRow>
            </RSColumn>
        </ReserveSettingsGenerateReportContainer>
    );
};
